import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { AlertTriangle, CalendarCheck, Info } from 'lucide-react';
import type { LeaveBalanceDto, LeavePreviewDto, LeaveTypeDto } from '@workforce/shared';
import { ApiError, api } from '@/lib/api';
import { fmtDays, ruleLines } from '@/lib/leave';
import { FileUpload, type UploadedFile } from './FileUpload';
import { fmtDate } from '@/lib/format';
import { t } from '@/lib/i18n';
import { Button, ErrorBanner, Field, Input, Select, Textarea, Toggle } from './ui';

interface Props {
  types: LeaveTypeDto[];
  balances?: LeaveBalanceDto[];
  /** Admin mode: record for another employee. */
  employeeId?: string;
  employeePicker?: React.ReactNode;
  adminMode?: boolean;
  onSubmitted: () => void;
  onCancel?: () => void;
}

/**
 * Leave request form with a live preview: working days, the days that are
 * not counted (weekends, holidays) and the balance before/after — shown
 * before the request is submitted.
 */
export function LeaveRequestForm({ types, balances, employeeId, employeePicker, adminMode, onSubmitted, onCancel }: Props) {
  const primary = types.find((x) => x.isPrimary) ?? types[0];
  const [leaveTypeId, setLeaveTypeId] = useState(primary?.id ?? '');
  const tomorrow = DateTime.now().plus({ days: 1 }).toISODate()!;
  const [startDate, setStartDate] = useState(tomorrow);
  const [endDate, setEndDate] = useState(tomorrow);
  const [startHalf, setStartHalf] = useState(false);
  const [endHalf, setEndHalf] = useState(false);
  const [reason, setReason] = useState('');
  const [approveNow, setApproveNow] = useState(false);
  const [doc, setDoc] = useState<UploadedFile | null>(null);
  const type = types.find((x) => x.id === leaveTypeId);
  const single = startDate === endDate;

  useEffect(() => {
    if (!leaveTypeId && primary) setLeaveTypeId(primary.id);
  }, [primary, leaveTypeId]);
  useEffect(() => {
    if (endDate < startDate) setEndDate(startDate);
  }, [startDate, endDate]);
  useEffect(() => {
    if (type && !type.allowHalfDays) {
      setStartHalf(false);
      setEndHalf(false);
    }
  }, [type]);

  const body = useMemo(
    () => ({
      leaveTypeId,
      startDate,
      endDate,
      startHalf: single ? startHalf : startHalf,
      endHalf: single ? (startHalf ? false : endHalf) : endHalf,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
      ...(doc && type?.requiresDocument ? { documentId: doc.id } : {}),
      ...(adminMode ? { employeeId, approveNow } : {}),
    }),
    [leaveTypeId, startDate, endDate, startHalf, endHalf, reason, adminMode, employeeId, approveNow, single, doc, type],
  );
  const ready = !!leaveTypeId && !!startDate && !!endDate && endDate >= startDate && (!adminMode || !!employeeId);
  const needsDocument = !!type?.requiresDocument && !doc;
  const rules = type ? ruleLines(type, types.find((x) => x.id === type.drawsFromTypeId)?.name) : [];

  const preview = useMutation({
    mutationFn: () => api.post<LeavePreviewDto>(adminMode ? '/leave/preview' : '/me/leave/preview', body),
  });
  // Debounced live preview.
  useEffect(() => {
    if (!ready) return;
    const timer = setTimeout(() => preview.mutate(), 250);
    return () => clearTimeout(timer);
  }, [JSON.stringify({ ...body, reason: undefined, documentId: undefined }), ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = useMutation({
    mutationFn: () => api.post(adminMode ? '/leave/requests' : '/me/leave/requests', body),
    onSuccess: () => onSubmitted(),
  });

  const p = preview.data;
  const blocking = !p || p.errors.length > 0 || preview.isPending;
  const balance = balances?.find((b) => b.leaveTypeId === leaveTypeId);
  const notCounted = p?.breakdown.filter((b) => b.fraction === 0 || b.kind === 'half_holiday') ?? [];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
      <div className="space-y-4 lg:col-span-3">
        {employeePicker}
        <Field label={t('Leave type')} required>
          <Select value={leaveTypeId} onChange={(e) => setLeaveTypeId(e.target.value)}>
            {types.map((lt) => {
              const b = balances?.find((x) => x.leaveTypeId === lt.id);
              return (
                <option key={lt.id} value={lt.id}>
                  {t(lt.name)}
                  {b && lt.deductsBalance ? ` — ${t('{days} left', { days: fmtDays(b.remaining) })}` : !lt.deductsBalance ? ` — ${t('no balance used')}` : ''}
                </option>
              );
            })}
          </Select>
        </Field>
        {type?.description && <p className="-mt-2 text-xs text-slate-500">{t(type.description)}</p>}
        {rules.length > 0 && (
          <div className="-mt-1 flex flex-wrap gap-1.5">
            {rules.map((r) => (
              <span key={r} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                {r}
              </span>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('First day')} required>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label={t('Last day')} required>
            <Input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>
        {type?.allowHalfDays && (
          <div className="space-y-2 rounded-lg border border-slate-200 p-3">
            {single ? (
              <div className="flex flex-wrap gap-4 text-sm">
                {(['full', 'am', 'pm'] as const).map((opt) => (
                  <label key={opt} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="portion"
                      checked={opt === 'full' ? !startHalf && !endHalf : opt === 'pm' ? startHalf : endHalf}
                      onChange={() => {
                        setStartHalf(opt === 'pm');
                        setEndHalf(opt === 'am');
                      }}
                    />
                    {opt === 'full' ? t('Full day') : opt === 'am' ? t('Morning only') : t('Afternoon only')}
                  </label>
                ))}
              </div>
            ) : (
              <>
                <Toggle checked={startHalf} onChange={setStartHalf} label={t('First day: afternoon only')} />
                <Toggle checked={endHalf} onChange={setEndHalf} label={t('Last day: morning only')} />
              </>
            )}
          </div>
        )}
        {type?.requiresDocument && (
          <Field label={t('Medical report')} required>
            <FileUpload kind="medical_report" employeeId={adminMode ? employeeId : undefined} value={doc} onChange={setDoc} label={t('Attach medical report')} />
          </Field>
        )}
        <Field label={t('Reason / comment')} hint={t('Optional. Visible to your manager and administrators.')}>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
        </Field>
        {adminMode && (
          <Toggle
            checked={approveNow}
            onChange={setApproveNow}
            label={t('Approve immediately')}
            description={t('Records the leave as approved (e.g. sick leave reported by phone). Leave off to send it for approval.')}
          />
        )}
      </div>

      <div className="lg:col-span-2">
        <div className="sticky top-20 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <CalendarCheck className="h-4 w-4 text-brand-600" /> {t('Summary')}
          </p>
          {balance && type?.deductsBalance && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label={t('Entitled')} value={balance.entitled + balance.carriedOver + balance.adjusted} />
              <Stat label={t('Used')} value={balance.used} />
              <Stat label={t('Pending')} value={balance.pending} />
            </div>
          )}
          {preview.error && <ErrorBanner error={preview.error} />}
          {p && (
            <>
              <div className="rounded-lg bg-surface p-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-slate-600">{t('Days requested')}</span>
                  <span className="text-2xl font-semibold text-slate-900">{p.days}</span>
                </div>
                {p.leaveType.deductsBalance &&
                  p.balances.map((b) => (
                    <div key={b.yearStart} className="mt-2 space-y-1 border-t border-slate-100 pt-2 text-sm">
                      {p.balances.length > 1 && (
                        <p className="text-xs text-slate-500">
                          {t('Leave year {start} – {end} ({days})', { start: fmtDate(b.yearStart, 'dd LLL yy'), end: fmtDate(b.yearEnd, 'dd LLL yy'), days: fmtDays(b.days) })}
                        </p>
                      )}
                      <div className="flex justify-between">
                        <span className="text-slate-600">{t('Available now')}</span>
                        <span className="font-medium">{fmtDays(b.available)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">{t('Balance after this request')}</span>
                        <span className={`font-semibold ${b.after < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmtDays(b.after)}</span>
                      </div>
                    </div>
                  ))}
              </div>
              {notCounted.length > 0 && (
                <div className="text-xs text-slate-600">
                  <p className="mb-1 font-medium text-slate-700">{t('Not counted')}</p>
                  <ul className="max-h-32 space-y-0.5 overflow-y-auto">
                    {notCounted.map((b) => (
                      <li key={b.date}>
                        {fmtDate(b.date, 'ccc dd LLL')} —{' '}
                        {b.kind === 'weekend' ? t('day off') : b.kind === 'holiday' ? b.holidayName : b.kind === 'half_holiday' ? t('{name} (half day)', { name: b.holidayName }) : t('not employed')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {p.errors.map((e, i) => (
                <p key={i} className="flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t(e)}
                </p>
              ))}
              {p.warnings.map((w, i) => (
                <p key={i} className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t(w)}
                </p>
              ))}
              {!p.errors.length && (
                <p className="text-xs text-slate-500">
                  {adminMode && approveNow
                    ? t('Will be recorded as approved.')
                    : p.leaveType.requiresApproval
                      ? t('Will be sent to your manager / an administrator for approval.')
                      : t('This leave type is approved automatically.')}
                </p>
              )}
            </>
          )}
          <ErrorBanner error={submit.error instanceof ApiError ? submit.error : submit.error} />
          <div className="flex gap-2">
            {onCancel && (
              <Button variant="secondary" className="flex-1" onClick={onCancel}>
                {t('Cancel')}
              </Button>
            )}
            <Button className="flex-1" disabled={!ready || blocking || needsDocument} loading={submit.isPending} onClick={() => submit.mutate()}>
              {adminMode && approveNow ? t('Record leave') : t('Submit request')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-surface px-2 py-1.5">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{Math.round(value * 100) / 100}</p>
    </div>
  );
}
